import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Grado } from 'src/app/models/grado.model';
import { Alumno } from 'src/app/models/alumno.model';
import { EmailPayload } from 'src/app/models/email.model';
import { GradoService } from 'src/app/services/grado.service';
import { AlumnoService } from 'src/app/services/alumno.service';
import { EmailService } from 'src/app/services/email.service';
import { EscuelaService } from 'src/app/services/escuela.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import Swal from 'sweetalert2';

export interface AlumnoItemEmail extends Alumno {
  seleccionado: boolean;
}

@Component({
  selector: 'app-email-grado',
  templateUrl: './email-grado.component.html',
  styleUrls: ['./email-grado.component.css']
})
export class EmailGradoComponent implements OnInit, OnDestroy {
  public grados: Grado[] = [];
  public gradosSeleccionadosIds: number[] = [];
  public alumnos: AlumnoItemEmail[] = [];

  public cargandoAlumnos: boolean = false;
  public enviandoEmail: boolean = false;

  public asunto: string = '';
  public cuerpo: string = '';
  public logEnvio: string = '';
  public todosSeleccionados: boolean = true;

  private subscriptions: Subscription[] = [];

  constructor(
    private gradoService: GradoService,
    private alumnoService: AlumnoService,
    private emailService: EmailService,
    private escuelaService: EscuelaService,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarGrados();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public cargarGrados(): void {
    const sub = this.gradoService
      .getGradosPorEscuelaNoPaging()
      .pipe(take(1))
      .subscribe({
        next: (resp: any) => {
          if (resp && resp.resultList) {
            this.grados = resp.resultList;
          } else if (Array.isArray(resp)) {
            this.grados = resp;
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error al cargar grados:', err);
          this.snackBarService.openSnackBar('Error al cargar listado de grados', 'Cerrar');
        }
      });
    this.subscriptions.push(sub);
  }

  public onGradosChange(): void {
    if (!this.gradosSeleccionadosIds || this.gradosSeleccionadosIds.length === 0) {
      this.alumnos = [];
      this.logEnvio = '';
      return;
    }

    this.cargandoAlumnos = true;
    this.alumnos = [];
    const cantGrados = this.gradosSeleccionadosIds.length;
    this.logEnvio = `[INFO] Consultando alumnos para ${cantGrados} grado(s) seleccionado(s)...\n`;

    const requests = this.gradosSeleccionadosIds.map((id) =>
      this.alumnoService.getAlumnosPorEscuelaYGrado(id).pipe(take(1))
    );

    const sub = forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        this.cargandoAlumnos = false;
        const alumnosMap = new Map<number, AlumnoItemEmail>();

        responses.forEach((resp) => {
          const list: Alumno[] = resp?.resultList || [];
          list.forEach((a) => {
            if (!alumnosMap.has(a.id)) {
              alumnosMap.set(a.id, {
                ...a,
                seleccionado: !!(a.eMail && a.eMail.trim() !== '')
              });
            }
          });
        });

        this.alumnos = Array.from(alumnosMap.values()).sort((a, b) => {
          const apeA = (a.apellido || '').toLowerCase();
          const apeB = (b.apellido || '').toLowerCase();
          if (apeA < apeB) return -1;
          if (apeA > apeB) return 1;
          const nomA = (a.nombre || '').toLowerCase();
          const nomB = (b.nombre || '').toLowerCase();
          return nomA.localeCompare(nomB);
        });

        const conEmail = this.totalConEmail;
        this.logEnvio += `[INFO] Alumnos cargados: ${this.alumnos.length} total (${conEmail} con correo electrónico registrado).\n`;
        this.todosSeleccionados = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.cargandoAlumnos = false;
        console.error('Error al obtener alumnos de los grados:', err);
        this.snackBarService.openSnackBar('Error al obtener alumnos de los grados', 'Cerrar');
        this.logEnvio += `[ERROR] No se pudieron cargar los alumnos de los grados seleccionados.\n`;
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(sub);
  }

  public seleccionarTodosLosGrados(): void {
    this.gradosSeleccionadosIds = this.grados.map((g) => g.id);
    this.onGradosChange();
  }

  public deseleccionarTodosLosGrados(): void {
    this.gradosSeleccionadosIds = [];
    this.onGradosChange();
  }

  public removerGrado(gradoId: number): void {
    this.gradosSeleccionadosIds = this.gradosSeleccionadosIds.filter((id) => id !== gradoId);
    this.onGradosChange();
  }

  public get totalAlumnos(): number {
    return this.alumnos.length;
  }

  public get totalConEmail(): number {
    return this.alumnos.filter((a) => !!a.eMail && a.eMail.trim() !== '').length;
  }

  public get totalSinEmail(): number {
    return this.alumnos.filter((a) => !a.eMail || a.eMail.trim() === '').length;
  }

  public get totalAlumnosSeleccionados(): number {
    return this.alumnos.filter((a) => a.seleccionado).length;
  }

  public get destinatariosSeleccionados(): AlumnoItemEmail[] {
    return this.alumnos.filter((a) => a.seleccionado && !!a.eMail && a.eMail.trim() !== '');
  }

  public get emailsBCC(): string[] {
    const setEmails = new Set<string>();
    this.destinatariosSeleccionados.forEach((a) => {
      const email = a.eMail?.trim();
      if (email) {
        setEmails.add(email);
      }
    });
    return Array.from(setEmails);
  }

  public toggleSeleccionarTodos(event?: any): void {
    if (event && typeof event.checked === 'boolean') {
      this.todosSeleccionados = event.checked;
    } else {
      this.todosSeleccionados = !this.todosSeleccionados;
    }
    this.alumnos.forEach((a) => {
      if (a.eMail && a.eMail.trim() !== '') {
        a.seleccionado = this.todosSeleccionados;
      }
    });
  }

  public onCheckboxIndividualChange(): void {
    const alumnosConMail = this.alumnos.filter((a) => !!a.eMail && a.eMail.trim() !== '');
    this.todosSeleccionados =
      alumnosConMail.length > 0 && alumnosConMail.every((a) => a.seleccionado);
  }

  public agregarTagAlCuerpo(tag: string): void {
    this.cuerpo += ` ${tag} `;
  }

  public get gradosSeleccionados(): Grado[] {
    return this.grados.filter((g) => this.gradosSeleccionadosIds.includes(g.id));
  }

  public get nombresGradosSeleccionados(): string {
    const seleccionados = this.gradosSeleccionados;
    if (seleccionados.length === 0) return '';
    return seleccionados.map((g) => g.descripcion || g.nombre).join(', ');
  }

  public async enviarEmailPorGrado(): Promise<void> {
    if (!this.gradosSeleccionadosIds || this.gradosSeleccionadosIds.length === 0) {
      Swal.fire('Atención', 'Por favor selecciona al menos un grado.', 'warning');
      return;
    }

    if (!this.asunto || this.asunto.trim() === '') {
      Swal.fire('Atención', 'El asunto del email es obligatorio.', 'warning');
      return;
    }

    if (!this.cuerpo || this.cuerpo.trim() === '') {
      Swal.fire('Atención', 'El cuerpo del mensaje es obligatorio.', 'warning');
      return;
    }

    const correosBCC = this.emailsBCC;
    if (correosBCC.length === 0) {
      Swal.fire(
        'Sin destinatarios',
        'No hay alumnos seleccionados con una dirección de correo válida.',
        'warning'
      );
      return;
    }

    const nombresGrados = this.nombresGradosSeleccionados || 'Grados seleccionados';
    const cantGrados = this.gradosSeleccionadosIds.length;
    const escuelaNombre = this.escuelaService.Escuela()?.nombre || 'Colegio';

    const confirmResult = await Swal.fire({
      title: '¿Confirmar envío de email?',
      html: `
        <p>Se enviará el correo a <b>${correosBCC.length}</b> destinatarios de <b>${cantGrados} grado(s)</b> (${nombresGrados}).</p>
        <p class="text-muted" style="font-size: 0.9em;">Los correos se enviarán con <b>copia oculta (BCC)</b> para resguardar la privacidad de los destinatarios.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, enviar correos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#007bff'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    this.enviandoEmail = true;
    this.logEnvio += `\n========================================\n`;
    this.logEnvio += `[INICIO] Proceso de envío iniciado: ${new Date().toLocaleTimeString()}\n`;
    this.logEnvio += `[DETALLE] Grado(s): ${nombresGrados}\n`;
    this.logEnvio += `[DETALLE] Asunto: ${this.asunto}\n`;
    this.logEnvio += `[DETALLE] Total destinatarios en copia oculta: ${correosBCC.length}\n`;
    this.logEnvio += `========================================\n`;

    // Reemplazo de variables dinámicas si existen
    const cuerpoFinal = this.cuerpo
      .replace(/{grado}/g, nombresGrados)
      .replace(/{colegio}/g, escuelaNombre);

    const payload: EmailPayload = {
      subject: this.asunto,
      destinatarios: correosBCC,
      body: cuerpoFinal
    };

    this.emailService.enviarEmail(payload).subscribe({
      next: (resp: any) => {
        this.enviandoEmail = false;
        const msg = resp?.mensaje || resp?.message || 'Email enviado con éxito';
        this.logEnvio += `[EXITO] ${msg} (${new Date().toLocaleTimeString()})\n`;
        this.logEnvio += `[INFO] Correo emitido a ${correosBCC.length} direcciones.\n`;

        Swal.fire({
          title: '¡Envío Exitoso!',
          text: `El email se envió correctamente a ${correosBCC.length} destinatarios de ${cantGrados} grado(s).`,
          icon: 'success'
        });

        this.snackBarService.openSnackBar('Email enviado correctamente', 'Cerrar');
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.enviandoEmail = false;
        const errMsg = err?.error?.message || err?.message || 'Error en el servidor al enviar los correos.';
        this.logEnvio += `[ERROR] ${errMsg} (${new Date().toLocaleTimeString()})\n`;
        console.error('Error al enviar email por grado:', err);

        Swal.fire('Error en el envío', errMsg, 'error');
        this.cdr.detectChanges();
      }
    });
  }

  public limpiarFormulario(): void {
    this.asunto = '';
    this.cuerpo = '';
    this.logEnvio = '';
    this.gradosSeleccionadosIds = [];
    this.alumnos = [];
    this.todosSeleccionados = true;
    this.cdr.detectChanges();
  }
}
